import { Formik } from "formik";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import Select from "../../components/Select";
import { findCollectionByKeys, normalizeOptions } from "../../utils/helper";
import { fetchIssueEquipmentDropdown, fetchIssueTyresDropdown, fetchIssueVehiclesDropdown, getIssueById, getIssueTagsDropdown, getServiceEntryDropdown } from "../../store/Slices/IssuesSlice";
import { clearEquipmentDetailState, fetchEquipmentServiceDetail } from "../../store/Slices/EquipmentSlice";
import { clearVehicleDetail, fetchNextServiceId, fetchVehicleDetail, updateServiceIssue } from "../../store/Slices/ServiceSlice";
import { clearTyreDetailState, fetchTyreServiceDetail } from "../../store/Slices/TyreSlice";
import { getUserDropdown } from "../../store/Slices/UserSlice";
import { priorityOptions, statusOptions } from "../../utils/Dropdowns";
import { formatUtcDateTimeForApi, formatUtcDateTimeForInput, hasPermission } from "../../utils/helper";
import { AppMainModules, MaintinaceSubModules, IssuePermission } from "../../utils/app-constant";
import { IssueCreateValidationSchema } from "../../utils/Schema";
import { errorNotify, successNotify } from "../../utils/toast";
import IssueHistory from "./IssueHistory";
import {
  AssetSourceTypeEnum,
  assetTypeOptions,
  extractLastRecordedMeterValue,
  getIssueAssetId,
  getIssueAssetLabel,
  getIssueAssetType,
  getPayloadId,
} from "./issueAsset.helpers";

const issueStatusOptions = statusOptions.filter((status) => status.value !== "completed");

const IssueDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    vehicleDetail,
    serviceUpdateLoading,
    nextServiceId,
  } = useSelector((state) => state.services);
  const {
    equipmentDetailData,
  } = useSelector((state) => state.equipment);
  const {
    tyreDetailData,
  } = useSelector((state) => state.tyre);
  const { userDropdownLoading, userDropdownData } = useSelector((state) => state.users);
  const {
    issueIdData,
    issueIdLoading,
    getIssueTagsDropdownData,
    getIssueTagsDropdownLoading,
    issueVehiclesDropdownData,
    issueVehiclesDropdownLoading,
    issueEquipmentDropdownData,
    issueEquipmentDropdownLoading,
    issueTyresDropdownData,
    issueTyresDropdownLoading,
  } = useSelector((state) => state.issues);

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const canUpdate = hasPermission({ permissionSlug: IssuePermission.Update, menuSlug: MaintinaceSubModules.Issue, moduleSlug: AppMainModules.maintenance });

  useEffect(() => {
    dispatch(getUserDropdown());
    dispatch(fetchIssueVehiclesDropdown());
    dispatch(fetchIssueEquipmentDropdown());
    dispatch(fetchIssueTyresDropdown());
    dispatch(getIssueById(id));
    dispatch(getServiceEntryDropdown());
    dispatch(getIssueTagsDropdown());

    return () => {
      dispatch(clearVehicleDetail());
      dispatch(clearEquipmentDetailState());
      dispatch(clearTyreDetailState());
    };
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(fetchNextServiceId());
  }, [dispatch]);

  useEffect(() => {
    if (issueIdData) {
      setUploadedFiles(
        (issueIdData.images || []).map((img) => ({
          fileName: img.file_path,
          originalName: img.file_path,
          previewUrl: null,
          backend: true,
          id: img.id
        }))
      );
    }
  }, [issueIdData]);

  const vehicleOptions = useMemo(() => {
    const source = Array.isArray(issueVehiclesDropdownData)
      ? issueVehiclesDropdownData
      : findCollectionByKeys(issueVehiclesDropdownData, [
        "vehicles",
        "vehicle",
        "vehiclesdropdown",
        "vehicledropdown",
        "vehiclelist",
      ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "vehicle_id", "ref_id"],
      labelKeys: ["label", "registration_number", "registration_no", "vehicle_registration_number", "name", "title"],
      defaultLabelPrefix: "Vehicle",
    });
  }, [issueVehiclesDropdownData]);

  const equipmentOptions = useMemo(() => {
    const source = Array.isArray(issueEquipmentDropdownData)
      ? issueEquipmentDropdownData
      : findCollectionByKeys(issueEquipmentDropdownData, [
        "equipments",
        "equipmentdropdown",
        "equipment",
        "equipmentlist",
      ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "equipment_id"],
      labelKeys: ["label", "equipment_id", "title", "name", "serial_number", "registration_number"],
      defaultLabelPrefix: "Equipment",
    });
  }, [issueEquipmentDropdownData]);

  const tyreOptions = useMemo(() => {
    const source = Array.isArray(issueTyresDropdownData)
      ? issueTyresDropdownData
      : findCollectionByKeys(issueTyresDropdownData, [
        "tyres",
        "tyre",
        "tyresdropdown",
        "tyredropdown",
        "tyrelist",
      ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "tyre_id", "system_id"],
      labelKeys: ["label", "tyre_id", "serial_number", "system_id", "name", "title"],
      defaultLabelPrefix: "Tyre",
    });
  }, [issueTyresDropdownData]);

  const tagsOptions = getIssueTagsDropdownData?.map(tag => ({ label: tag.tag_name, value: tag.tag_name }));

  const seOptions = issueIdData?.vehicle_services?.map((item) => ({
      label: item?.service_id,
      value: item?.id,
    })) || [];

  const userOptions =
    userDropdownData &&
    userDropdownData.map((user) => ({
      value: user.value,
      label: user.label,
      image: user.image,
      showIcon: true,
    }));

  const resolveAssetDetailServiceId = useCallback(async () => {
    const normalizedIssueServiceId = getPayloadId(issueIdData?.service_id);
    if (
      normalizedIssueServiceId !== "" &&
      normalizedIssueServiceId !== null &&
      normalizedIssueServiceId !== undefined &&
      Number(normalizedIssueServiceId) > 0
    ) {
      return normalizedIssueServiceId;
    }

    const normalizedNextServiceId = getPayloadId(nextServiceId);
    if (
      normalizedNextServiceId !== "" &&
      normalizedNextServiceId !== null &&
      normalizedNextServiceId !== undefined
    ) {
      return normalizedNextServiceId;
    }

    const response = await dispatch(fetchNextServiceId()).unwrap();
    return getPayloadId(response?.data ?? response ?? 0);
  }, [dispatch, issueIdData?.service_id, nextServiceId]);

  const getCurrentAssetDetail = (assetType) => {
    if (assetType === AssetSourceTypeEnum.Vehicle) {
      return vehicleDetail;
    }

    if (assetType === AssetSourceTypeEnum.Equipment) {
      return equipmentDetailData;
    }

    if (assetType === AssetSourceTypeEnum.Tyre) {
      return tyreDetailData;
    }

    return null;
  };

  useEffect(() => {
    if (!issueIdData) {
      return;
    }

    const assetType = getIssueAssetType(issueIdData);
    const assetId = getPayloadId(getIssueAssetId(issueIdData));

    if (assetId === "" || assetId === null || assetId === undefined || !assetType) {
      dispatch(clearVehicleDetail());
      dispatch(clearEquipmentDetailState());
      dispatch(clearTyreDetailState());
      return;
    }

    let isSubscribed = true;

    const loadAssetDetail = async () => {
      dispatch(clearVehicleDetail());
      dispatch(clearEquipmentDetailState());
      dispatch(clearTyreDetailState());

      try {
        if (assetType === AssetSourceTypeEnum.Vehicle) {
          await dispatch(fetchVehicleDetail({
            vehicleId: assetId,
            vehicleBrandId:
              issueIdData?.vehicle?.make_id ||
              issueIdData?.vehicle?.brand_id ||
              issueIdData?.vehicle_brand_id,
            ref_id: issueIdData?.service_id || issueIdData?.id,
          })).unwrap();
          return;
        }

        const serviceId = await resolveAssetDetailServiceId();
        if (!isSubscribed) {
          return;
        }

        if (assetType === AssetSourceTypeEnum.Equipment) {
          await dispatch(fetchEquipmentServiceDetail({
            equipmentId: assetId,
            serviceId,
          })).unwrap();
          return;
        }

        if (assetType === AssetSourceTypeEnum.Tyre) {
          await dispatch(fetchTyreServiceDetail({
            tyreId: assetId,
            serviceId,
          })).unwrap();
        }
      } catch (error) {
        if (isSubscribed) {
          errorNotify(error);
        }
      }
    };

    loadAssetDetail();

    return () => {
      isSubscribed = false;
    };
  }, [dispatch, issueIdData, nextServiceId, resolveAssetDetailServiceId]);

  const initialValues = {
    asset_type: getIssueAssetType(issueIdData),
    asset_id: getPayloadId(getIssueAssetId(issueIdData)),
    service_id: issueIdData?.service_id ?? "",
    reported_by: issueIdData?.reported_by ?? "",
    assign_to: issueIdData?.assign_to ?? "",
    odo_meter: issueIdData?.last_service?.odo_meter ?? null,
    odm: issueIdData?.odo_meter ?? "",
    priority: issueIdData?.priority ?? "",
    status: issueIdData?.status ?? "",
    summery: issueIdData?.summery ?? "",
    description: issueIdData?.description ?? "",
    reported_at: formatUtcDateTimeForInput(issueIdData?.reported_at),
    tags:
      issueIdData?.issue_tags?.map((tag) => ({
        value: tag.tag_name,
        label: tag.tag_name,
      })) || [],
    files: [],
    link_service_ids: issueIdData?.services?.map((s) => s.id) || [],
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const changedKeys = Object.keys(values).filter((key) => {
        return !_.isEqual(values[key], initialValues[key]);
      });

      const hasNewFiles = uploadedFiles.some((file) => file.previewUrl);

      if (hasNewFiles && !changedKeys.includes("files")) {
        changedKeys.push("files");
      }

      const issueData = {
        asset_id: getPayloadId(values.asset_id),
        asset_type: values.asset_type,
        issue_id: parseInt(id),
        service_id: parseInt(id),
        service_issue_id: issueIdData?.service_issue_id,
        reported_by: parseInt(values.reported_by),
        assign_to: parseInt(values.assign_to),
        odo_meter: parseInt(values.odm),
        priority: values.priority,
        issue_type: issueIdData?.issue_type,
        status: values.status,
        summery: values.summery,
        description: values.description,
        reported_at: formatUtcDateTimeForApi(values.reported_at),
        tags: values.tags.map((tag) => tag.value || tag),
        files: uploadedFiles.map((file) => file.fileName),
        link_service_ids: values.link_service_ids,
        updated_keys: changedKeys,
      };
      await dispatch(updateServiceIssue(issueData)).unwrap();
      successNotify("Issue Updated successfully");
      resetForm();
      navigate("/issue");
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (issueIdLoading) {
    return (
      <div className="loading_spinner">
        <Spinner animation="grow" />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumbs values={["Issues", "Overview", "Issue Details"]} />

      <Formik
        initialValues={initialValues}
        validationSchema={IssueCreateValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          submitForm,
        }) => {
          const selectedAssetType = values.asset_type || "";
          const currentAssetOptions = selectedAssetType === AssetSourceTypeEnum.Vehicle
            ? vehicleOptions
            : selectedAssetType === AssetSourceTypeEnum.Equipment
              ? equipmentOptions
              : selectedAssetType === AssetSourceTypeEnum.Tyre
                ? tyreOptions
                : [];
          const currentAssetLoading = selectedAssetType === AssetSourceTypeEnum.Vehicle
            ? issueVehiclesDropdownLoading
            : selectedAssetType === AssetSourceTypeEnum.Equipment
              ? issueEquipmentDropdownLoading
              : selectedAssetType === AssetSourceTypeEnum.Tyre
                ? issueTyresDropdownLoading
                : false;
          const currentAssetLabel = selectedAssetType === AssetSourceTypeEnum.Vehicle
            ? "Vehicle"
            : selectedAssetType === AssetSourceTypeEnum.Equipment
              ? "Equipment"
              : selectedAssetType === AssetSourceTypeEnum.Tyre
                ? "Tyre"
                : "Asset";
          const currentAssetPlaceholder = selectedAssetType
            ? `Select ${currentAssetLabel}`
            : "Select Asset Type First";
          const fallbackSelectedAssetOption =
            values.asset_id !== "" && values.asset_id !== null && values.asset_id !== undefined
              ? {
                value: values.asset_id,
                label: getIssueAssetLabel(issueIdData, selectedAssetType) || `${currentAssetLabel} ${values.asset_id}`,
              }
              : null;
          const selectedAssetValue =
            currentAssetOptions.find(
              (option) => String(option.value) === String(values.asset_id)
            ) ||
            fallbackSelectedAssetOption ||
            null;
          const currentAssetDetail = getCurrentAssetDetail(selectedAssetType);
          const currentLastRecordedMeter = extractLastRecordedMeterValue(currentAssetDetail);

          return (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <div className="access_view">
              <h2>Issue Details</h2>
              <div className="service_entry_btn">
                <Button
                  cancelBtn={() => {
                    navigate(-1);
                  }}
                  loading={isSubmitting || serviceUpdateLoading}
                  disabled={isSubmitting || serviceUpdateLoading}
                  submitText="Save Details"
                  type="submit"
                  shouldRow={false}
                  buttonExtraCss="save_issue_details_btn"
                  isShowSubmitBtn={canUpdate}
                />
              </div>
            </div>

            <div className="service_entry_main">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-md-8">
                    <div className="service_entry_details">
                      <h6>Issue Main Information</h6>
                      <p>Comprehensive information for this issue record.</p>

                      <div className="vehicle_details_fields">
                        <Row>
                          <Col md={12}>
                            <div className="show_issue_id">
                              Issue ID: <span>SI-{id}</span>
                            </div>
                          </Col>
                          <Col md={12}>
                            <Input
                              label="Summary"
                              placeholder="Brief overview of the issue"
                              type="text"
                              name="summery"
                              value={values.summery}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.summery}
                              touched={touched.summery}
                            />
                          </Col>
                          <Col md={12}>
                            <Input
                              label="Description (Optional)"
                              placeholder="Add issue description"
                              type="text"
                              name="description"
                              value={values.description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.description}
                              touched={touched.description}
                              quill={true}
                              setFieldValue={setFieldValue}
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>

                    <div
                      className="service_entry_details"
                      style={{ marginTop: "20px" }}
                    >
                      <h6>Service Entry</h6>
                      <p>
                        Upload relevant photos or documents for this service.
                      </p>

                      <Row>
                        <Col md={12}>
                          <Select
                            label="Link Service Entry"
                            placeholder="Please Select Link Service Entry"
                            options={seOptions}
                            loading={userDropdownLoading}
                            name="link_service_ids"
                            value={seOptions?.filter((option) =>
                              values.link_service_ids.includes(option.value)
                            )}
                            onChange={(selectedOptions) =>
                              setFieldValue(
                                "link_service_ids",
                                selectedOptions
                                  ? selectedOptions.map((opt) => opt.value)
                                  : []
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.link_service_ids}
                            touched={touched.link_service_ids}
                            isSearch={false}
                            isMulti={true}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div
                      className="service_entry_details"
                      style={{ marginTop: "20px" }}
                    >
                      <h6>Other Details</h6>
                      <p>Edit information for this issue record.</p>

                      <Row>
                        <Col md={6}>
                          <Select
                            label="Reporter"
                            placeholder="Please Select"
                            options={userOptions}
                            loading={userDropdownLoading}
                            name="reported_by"
                            value={userOptions.find(
                              (option) => option.value === values.reported_by
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "reported_by",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.reported_by}
                            touched={touched.reported_by}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>

                        <Col md={6}>
                          <Select
                            label="Assignee"
                            placeholder="Please Select"
                            options={userOptions}
                            loading={userDropdownLoading}
                            name="assign_to"
                            value={userOptions.find(
                              (option) => option.value === values.assign_to
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "assign_to",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.assign_to}
                            touched={touched.assign_to}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Priority"
                            placeholder="Select Priority"
                            options={priorityOptions}
                            loading={false}
                            name="priority"
                            value={priorityOptions.find(
                              (option) => option.value === values.priority
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "priority",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.priority}
                            touched={touched.priority}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Status"
                            placeholder="Select Status"
                            options={issueStatusOptions}
                            loading={false}
                            name="status"
                            value={statusOptions.find(
                              (option) => option.value === values.status
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "status",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.status}
                            touched={touched.status}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Reported Date & Time"
                            placeholder=""
                            type="datetime-local"
                            name="reported_at"
                            value={values.reported_at}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.reported_at}
                            touched={touched.reported_at}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Assets Type"
                            placeholder="Select Asset Type"
                            options={assetTypeOptions}
                            loading={false}
                            name="asset_type"
                            value={assetTypeOptions.find(
                              (option) => option.value === values.asset_type
                            ) || null}
                            onChange={() => { }}
                            onBlur={handleBlur}
                            error={errors.asset_type}
                            touched={touched.asset_type}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label={currentAssetLabel}
                            placeholder={currentAssetPlaceholder}
                            options={currentAssetOptions}
                            loading={currentAssetLoading}
                            name="asset_id"
                            value={selectedAssetValue}
                            onChange={() => { }}
                            onBlur={handleBlur}
                            error={errors.asset_id}
                            touched={touched.asset_id}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Odometer Reading / Distance"
                            placeholder="Enter Odometer Reading / Distance"
                            type="number"
                            name="odm"
                            showExtraText={
                              currentLastRecordedMeter !== "" &&
                              currentLastRecordedMeter !== null &&
                              currentLastRecordedMeter !== undefined
                            }
                            extraText={`Last Recorded: ${currentLastRecordedMeter || 0} KM`}
                            value={values.odm}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.odm}
                            touched={touched.odm}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Tags"
                            placeholder="Add Tags"
                            options={tagsOptions}
                            loading={getIssueTagsDropdownLoading}
                            name="tags"
                            value={values.tags}
                            onChange={(selectedOptions) =>
                              setFieldValue("tags", selectedOptions || [])
                            }
                            onBlur={handleBlur}
                            isMulti={true}
                            isCreatable={true}
                            error={
                              errors.tags && typeof errors.tags === "string"
                                ? errors.tags
                                : null
                            }
                            touched={touched.tags}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div className="photo_document_section mb-3">
                      <h6>Photos & Documents</h6>
                      <p>
                        Upload relevant photos or documents for this service.
                      </p>
                      <FileUpload
                        destination="service-entry"
                        files={uploadedFiles}
                        setFiles={setUploadedFiles}
                        maxFileSize={10}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <IssueHistory />
                  </div>
                </div>
              </div>
            </div>
          </form>
        );
        }}
      </Formik>
    </div>
  );
};

export default IssueDetails;
